import React, { useState, useEffect, useCallback } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Sparkles,
  Beer,
  Sun,
  Sunrise,
  Coffee,
  Zap,
  CalendarDays,
  BookmarkPlus,
} from 'lucide-react';
import {
  getPromotionRules,
  createPromotionRule,
  updatePromotionRule,
  deletePromotionRule,
  togglePromotionRule,
  getMenu,
  getOrgTemplates,
  createOrgTemplate,
  deleteOrgTemplate,
} from '../services/api';
import { storage } from '../utils/storage';
import MenuNav from '../components/MenuNav';
import TemplatePicker from '../components/TemplatePicker';
import { EMPTY_FORM, ruleToForm, formToRule, ruleToUpdateBody } from '../utils/promoMapper';
import { PROMO_TEMPLATES } from '../data/promoTemplates';
import {
  templateToForm,
  describeDays,
  describeHours,
  describeDiscount,
} from '../utils/promoTemplates';
import {
  ORG_TEMPLATE_KINDS,
  buildPromoTemplatePayload,
  promoFormFromTemplatePayload,
  describeOrgTemplate,
} from '../utils/orgTemplates';
import './PromotionsPage.css';

// Alineado con MAX_ACTIVE_RULES del backend (services/menu/shared/promoEvaluator.js).
// El backend cuenta todas las reglas no-eliminadas (activas + inactivas) <= 5.
const MAX_RULES = 5;
const PROMO_TYPES = ['happy_hour', 'early_bird', 'off_peak', 'flash'];
const DISCOUNT_TYPES = ['percentage', 'fixed'];
const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Iconos de las plantillas de promociones (NER-4488 fase 2).
const TEMPLATE_ICONS = { Beer, Sun, Sunrise, Coffee, Zap, CalendarDays };

function PromoTemplateIcon({ name }) {
  const Icon = TEMPLATE_ICONS[name] || Sparkles;
  return <Icon size={20} aria-hidden="true" />;
}

function PromotionsPage() {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuData, setMenuData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [ownTemplates, setOwnTemplates] = useState([]);
  const [ownLoading, setOwnLoading] = useState(false);
  const [templateNotice, setTemplateNotice] = useState('');
  // ruleId que se esta guardando como plantilla: evita el doble clic.
  const [savingTemplateFor, setSavingTemplateFor] = useState(null);
  const [restaurantId, setRestaurantId] = useState(storage.get('selectedRestaurantId') || '');

  useEffect(() => {
    const handleRestaurantChange = () => {
      const id = storage.get('selectedRestaurantId');
      setRestaurantId(id);
    };
    window.addEventListener('restaurantChanged', handleRestaurantChange);
    window.addEventListener('storage', handleRestaurantChange);
    return () => {
      window.removeEventListener('restaurantChanged', handleRestaurantChange);
      window.removeEventListener('storage', handleRestaurantChange);
    };
  }, []);

  const loadData = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [rulesData, menuResponse] = await Promise.all([getPromotionRules(), getMenu()]);
      setRules(rulesData.rules || []);
      setMenuData(menuResponse.menu || []);
    } catch (err) {
      console.error('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // El backend limita las reglas no-eliminadas (activas + inactivas) a MAX_RULES.
  // El listado ya excluye las 'deleted', así que rules.length es ese total.
  const ruleCount = rules.length;

  const handleOpenCreate = () => {
    setEditingRule(null);
    setForm({ ...EMPTY_FORM });
    setError('');
    setShowModal(true);
  };

  // Plantillas propias de la organización (NER-4488 fase 3).
  const loadOwnTemplates = useCallback(async () => {
    try {
      setOwnLoading(true);
      const data = await getOrgTemplates(ORG_TEMPLATE_KINDS.PROMO_RULE);
      setOwnTemplates(data.templates || []);
    } catch (err) {
      console.error('Error loading organization templates:', err);
      setOwnTemplates([]);
    } finally {
      setOwnLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOwnTemplates();
  }, [loadOwnTemplates]);

  const flashNotice = (message) => {
    setTemplateNotice(message);
    setTimeout(() => setTemplateNotice(''), 4000);
  };

  const handleSaveRuleAsTemplate = async (rule) => {
    if (savingTemplateFor) return;
    setSavingTemplateFor(rule.ruleId);
    try {
      const result = await createOrgTemplate({
        kind: ORG_TEMPLATE_KINDS.PROMO_RULE,
        name: rule.name,
        payload: buildPromoTemplatePayload(rule, menuData),
      });
      await loadOwnTemplates();
      flashNotice(
        result?.existing
          ? t('templates.own.alreadyExists', { name: rule.name })
          : t('templates.own.saved', { name: rule.name })
      );
    } catch (err) {
      console.error('Error saving organization template:', err);
      flashNotice(err.response?.data?.error || t('templates.own.saveError'));
    } finally {
      setSavingTemplateFor(null);
    }
  };

  const handleUseOwnTemplate = (templateId) => {
    if (ruleCount >= MAX_RULES) return;
    const tpl = ownTemplates.find((item) => item.templateId === templateId);
    if (!tpl) return;
    setEditingRule(null);
    setForm(promoFormFromTemplatePayload(tpl.payload, menuData, tpl.name));
    setError('');
    setShowTemplates(false);
    setShowModal(true);
  };

  const handleDeleteOwnTemplate = async (templateId) => {
    try {
      await deleteOrgTemplate(templateId);
      await loadOwnTemplates();
      flashNotice(t('templates.own.deleted'));
    } catch (err) {
      console.error('Error deleting organization template:', err);
      flashNotice(err.response?.data?.error || t('templates.own.saveError'));
    }
  };

  // Plantillas (NER-4488 fase 2): precargan el modal de nueva regla.
  const handleUseTemplate = (templateId) => {
    if (ruleCount >= MAX_RULES) return;
    const nextForm = templateToForm(templateId, menuData, t);
    if (!nextForm) return;
    setEditingRule(null);
    setForm(nextForm);
    setError('');
    setShowTemplates(false);
    setShowModal(true);
  };

  const handleOpenEdit = (rule) => {
    setEditingRule(rule);
    setForm(ruleToForm(rule));
    setError('');
    setShowModal(true);
  };

  useEscapeKey(Boolean(showDeleteConfirm), () => setShowDeleteConfirm(null));

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRule(null);
    setError('');
  };
  useEscapeKey(showModal, handleCloseModal);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      setSaving(true);
      setError('');
      const body = formToRule(form);
      if (editingRule) {
        await updatePromotionRule(editingRule.ruleId, body);
      } else {
        await createPromotionRule(body);
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving promotion rule:', err);
      setError(err.response?.data?.error || t('promotions.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule) => {
    try {
      setError('');
      const nextStatus = rule.status === 'active' ? 'inactive' : 'active';
      await togglePromotionRule(rule.ruleId, ruleToUpdateBody(rule, { status: nextStatus }));
      await loadData();
    } catch (err) {
      console.error('Error toggling rule:', err);
      setError(err.response?.data?.error || t('promotions.saveError'));
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await deletePromotionRule(ruleId);
      setShowDeleteConfirm(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  const handleDayToggle = (day) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
  };

  const handleItemToggle = (itemId) => {
    setForm((prev) => ({
      ...prev,
      itemIds: prev.itemIds.includes(itemId)
        ? prev.itemIds.filter((id) => id !== itemId)
        : [...prev.itemIds, itemId],
    }));
  };

  const getPromoTypeLabel = (type) => t(`promotions.types.${type}`);
  const getDayLabel = (day) => t(`promotions.days.${day}`);

  if (loading) {
    return (
      <div className="page-container">
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  const limitReached = ruleCount >= MAX_RULES;
  const limitHint = t('promotions.limitReachedHint', { max: MAX_RULES });

  return (
    <div className="page-container">
      <MenuNav />
      <div className="promotions-header">
        <div>
          <h1>{t('promotions.title')}</h1>
          <span className="promotions-limit">
            {t('promotions.activeLimit', { count: ruleCount, max: MAX_RULES })}
          </span>
          {limitReached && (
            <span className="promotions-limit__hint" role="status">
              {limitHint}
            </span>
          )}
        </div>
        <div className="promotions-header__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowTemplates((prev) => !prev)}
            disabled={limitReached}
            title={limitReached ? limitHint : undefined}
            aria-expanded={showTemplates || rules.length === 0}
          >
            <Sparkles size={16} aria-hidden="true" />
            {t('templates.useTemplate')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOpenCreate}
            disabled={limitReached}
            title={limitReached ? limitHint : undefined}
          >
            <Plus size={18} />
            {t('promotions.addRule')}
          </button>
        </div>
      </div>

      {templateNotice && (
        <div className="promotions-notice" role="status">
          {templateNotice}
        </div>
      )}

      {(showTemplates || rules.length === 0) && ruleCount < MAX_RULES && (
        <TemplatePicker
          title={t('templates.promos.title')}
          hint={rules.length === 0 ? t('templates.promos.emptyHint') : t('templates.promos.hint')}
          helpTopicKey="menu.promoTemplates"
          templates={PROMO_TEMPLATES.map((tpl) => ({
            id: tpl.id,
            icon: <PromoTemplateIcon name={tpl.icon} />,
            name: t(`promoTemplates.${tpl.id}.name`),
            description: t(`promoTemplates.${tpl.id}.description`),
            chips: [
              getPromoTypeLabel(tpl.type),
              describeDays(tpl.days, t),
              describeHours(tpl.start, tpl.end, t),
              describeDiscount(tpl.discountType, tpl.discountValue),
              tpl.categoryHints.length > 0
                ? t('templates.chips.targetHint')
                : t('templates.chips.wholeMenu'),
            ],
          }))}
          onSelect={handleUseTemplate}
          onClose={rules.length === 0 ? undefined : () => setShowTemplates(false)}
          ownTemplates={ownTemplates.map((tpl) => ({
            id: tpl.templateId,
            name: tpl.name,
            description: tpl.description,
            chips: describeOrgTemplate(tpl, t),
          }))}
          onSelectOwn={handleUseOwnTemplate}
          onDeleteOwn={handleDeleteOwnTemplate}
          ownLoading={ownLoading}
        />
      )}

      {error && !showModal && (
        <div className="promotions-error" role="alert">
          {error}
        </div>
      )}

      {rules.length === 0 ? (
        <div className="promotions-empty">
          <p>{t('promotions.empty')}</p>
        </div>
      ) : (
        <div className="promotions-list">
          {rules.map((rule) => {
            const isActive = rule.status === 'active';
            const schedule = rule.schedule || {};
            const discount = rule.discount || {};
            return (
              <div
                key={rule.ruleId}
                className={`promotions-card ${!isActive ? 'promotions-card--inactive' : ''}`}
              >
                <div className="promotions-card__header">
                  <div className="promotions-card__info">
                    <h3 className="promotions-card__name">{rule.name}</h3>
                    <span className={`promotions-card__type promotions-card__type--${rule.type}`}>
                      {getPromoTypeLabel(rule.type)}
                    </span>
                  </div>
                  <div className="promotions-card__actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleSaveRuleAsTemplate(rule)}
                      disabled={savingTemplateFor === rule.ruleId}
                      aria-busy={savingTemplateFor === rule.ruleId}
                      title={t('templates.saveAsTemplate')}
                      aria-label={t('templates.saveAsTemplate')}
                    >
                      <BookmarkPlus size={16} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleToggle(rule)}
                      title={isActive ? t('promotions.deactivate') : t('promotions.activate')}
                    >
                      {isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleOpenEdit(rule)}
                      title={t('common.edit')}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowDeleteConfirm(rule.ruleId)}
                      title={t('common.delete')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="promotions-card__details">
                  <div className="promotions-card__detail">
                    <span className="promotions-card__label">{t('promotions.schedule')}</span>
                    <span>
                      {(schedule.days || []).map((d) => getDayLabel(d)).join(', ')} &middot;{' '}
                      {schedule.start} - {schedule.end}
                    </span>
                  </div>
                  <div className="promotions-card__detail">
                    <span className="promotions-card__label">{t('promotions.discount')}</span>
                    <span>
                      {discount.type === 'percentage' ? `${discount.value}%` : `$${discount.value}`}
                    </span>
                  </div>
                </div>

                {/* Delete confirmation */}
                {showDeleteConfirm === rule.ruleId && (
                  <div className="promotions-card__delete-confirm">
                    <p>{t('promotions.deleteConfirm')}</p>
                    <div className="promotions-card__delete-actions">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowDeleteConfirm(null)}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(rule.ruleId)}
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRule ? t('promotions.editRule') : t('promotions.addRule')}</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <span className="material-icons">close</span>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="promotions-error" role="alert">
                  {error}
                </div>
              )}
              {/* Name */}
              <div className="form-group">
                <label>{t('promotions.ruleName')}</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t('promotions.ruleNamePlaceholder')}
                />
              </div>

              {/* Type */}
              <div className="form-group">
                <label>{t('promotions.promoType')}</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                >
                  {PROMO_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {getPromoTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Days of week */}
              <div className="form-group">
                <label>{t('promotions.daysOfWeek')}</label>
                <div className="promotions-days">
                  {DAYS_OF_WEEK.map((day) => (
                    <label key={day} className="promotions-day-checkbox">
                      <input
                        type="checkbox"
                        checked={form.days.includes(day)}
                        onChange={() => handleDayToggle(day)}
                      />
                      <span>{getDayLabel(day)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Time range */}
              <div className="form-group form-group--row">
                <div className="form-group">
                  <label>{t('promotions.startTime')}</label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>{t('promotions.endTime')}</label>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))}
                  />
                </div>
              </div>

              {/* Discount */}
              <div className="form-group form-group--row">
                <div className="form-group">
                  <label>{t('promotions.discountType')}</label>
                  <select
                    value={form.discountType}
                    onChange={(e) => setForm((prev) => ({ ...prev, discountType: e.target.value }))}
                  >
                    {DISCOUNT_TYPES.map((dt) => (
                      <option key={dt} value={dt}>
                        {t(`promotions.discountTypes.${dt}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('promotions.discountValue')}</label>
                  <input
                    type="number"
                    min="0"
                    value={form.discountValue}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        discountValue: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Apply to: categories and items */}
              <div className="form-group">
                <label>{t('promotions.applyTo')}</label>
                <div className="promotions-apply-to">
                  {menuData.map((category) => (
                    <div key={category.categoryId} className="promotions-category-group">
                      <label className="promotions-category-checkbox">
                        <input
                          type="checkbox"
                          checked={form.categoryIds.includes(category.categoryId)}
                          onChange={() => handleCategoryToggle(category.categoryId)}
                        />
                        <strong>{category.name}</strong>
                      </label>
                      {!form.categoryIds.includes(category.categoryId) && category.items && (
                        <div className="promotions-items-list">
                          {category.items.map((item) => (
                            <label key={item.itemId} className="promotions-item-checkbox">
                              <input
                                type="checkbox"
                                checked={form.itemIds.includes(item.itemId)}
                                onChange={() => handleItemToggle(item.itemId)}
                              />
                              <span>{item.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={handleCloseModal}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
              >
                {saving ? t('common.loading') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionsPage;
